import { Select, SkeletonText } from '@dev-spendesk/grapes';

import { useCustomFieldsQuery } from 'modules/budgets/apis';
import { QueryError } from 'src/core/common/components/QueryError';
import { QuerySuspense } from 'src/core/common/components/QuerySuspense';
import {
  type TGlobalFunctionTyped,
  useTranslation,
} from 'src/core/common/hooks/useTranslation';
import { type CustomFieldDefinition } from 'src/core/modules/budgets/models/customFieldDefinition';

import { type Dimension } from '../../approvalWorkflow';
import { useGetDimensions } from '../../hooks/useGetDimensions';
import { type CustomNode } from '../../node';
import { useWorkflowContext } from '../../state/WorkflowContext';

export const DimensionSelect = ({
  value,
  onChange,
}: {
  value: { key: string; label: string } | undefined;
  onChange: (
    value:
      | { type: Exclude<Dimension['type'], 'customField'> }
      | { type: 'customField'; customFieldId: string },
  ) => void;
}) => {
  const dimensions = useGetDimensions();
  const customFieldsQueryState = useCustomFieldsQuery();
  const { t } = useTranslation('global');
  const { getAllParentNodes, selectedNodeId, getSiblingNodes } =
    useWorkflowContext();

  const sibling = getSiblingNodes(selectedNodeId ?? '').find(
    (node) => node.type === 'baseNode' && node.id !== selectedNodeId,
  );
  const siblingDimension =
    sibling?.type === 'baseNode' ? sibling.data.dimension : undefined;

  return (
    <QuerySuspense
      queryState={customFieldsQueryState}
      loading={<SkeletonText size="xxl" width="full" />}
      fallback={(error) => (
        <QueryError componentType="Callout" queryError={error} />
      )}
    >
      {(customFields) => {
        const filteredOptions = getFilteredOptions({
          t,
          selectedParentNodes: getAllParentNodes(selectedNodeId ?? ''),
          siblingDimension,
          dimensions,
          customFields: customFields.customFields,
        });

        return (
          <Select
            name="dimension"
            options={filteredOptions}
            value={value}
            onSelect={(option: {
              key: string;
              label: string;
              type: Dimension['type'];
            }) => {
              if (option.type === 'customField') {
                onChange({ type: 'customField', customFieldId: option.key });
              } else {
                onChange({
                  type: option.key as Exclude<Dimension['type'], 'customField'>,
                });
              }
            }}
            fit="parent"
            placeholder={t('approvalWorkflows.select.dimension.placeholder')}
          />
        );
      }}
    </QuerySuspense>
  );
};

export const getFilteredOptions = ({
  t,
  selectedParentNodes,
  siblingDimension,
  dimensions,
  customFields,
}: {
  t: TGlobalFunctionTyped;
  selectedParentNodes: CustomNode[];
  siblingDimension: Dimension | undefined;
  dimensions: Dimension[];
  customFields: CustomFieldDefinition[];
}) => {
  if (siblingDimension) {
    // if there is a sibling dimension selected, we set this dimension to the same as the sibling dimension
    return getDimensionOptions(t, dimensions, customFields).filter(
      (option) => option.key === siblingDimension.type,
    );
  }

  // filter out options selected in the parent node
  const parentDimensions = selectedParentNodes
    ?.map((node) =>
      node.type === 'baseNode' ? node.data.dimension : undefined,
    )
    .filter((dimension): dimension is Dimension => dimension !== undefined);

  return getDimensionOptions(t, dimensions, customFields).filter((option) => {
    // For custom fields, check both the type and the specific customFieldId
    if (option.type === 'customField') {
      return !parentDimensions.some(
        (dimension) =>
          dimension.type === 'customField' &&
          dimension.customFieldId === option.key,
      );
    }
    // For regular dimensions, just check the type
    return !parentDimensions.some((dimension) => dimension.type === option.key);
  });
};

export const getDimensionOptions = (
  t: TGlobalFunctionTyped,
  dimensions: Dimension[],
  customFields: CustomFieldDefinition[],
) => {
  return dimensions.map((dimension) => {
    if (dimension.type === 'customField') {
      const customField = customFields.find(
        (field) => field.id === dimension.customFieldId,
      );
      return {
        type: 'customField',
        key: dimension.customFieldId,
        label:
          customField?.name ??
          t('approvalWorkflows.dimensions.customField.title'),
      };
    }
    return {
      type: dimension.type,
      key: dimension.type,
      label: getDimensionTranslation(dimension.type, t),
    };
  });
};

const getDimensionTranslation = (
  dimensionName: Dimension['type'],
  t: TGlobalFunctionTyped,
) => {
  switch (dimensionName) {
    case 'spendType':
      return t('approvalWorkflows.dimensions.spendType.title');
    case 'costCenter':
      return t('approvalWorkflows.dimensions.costCenter.title');
    case 'team':
      return t('approvalWorkflows.dimensions.team.title');
    case 'customField':
      return t('approvalWorkflows.dimensions.customField.title');
    case 'expenseCategory':
      return t('approvalWorkflows.dimensions.expenseCategory.title');
    default:
      return dimensionName;
  }
};
